import { Integration } from '@botpress/client'
import { SchemerTree } from '@bpinternal/schemer'
import { OpenApiSchemaObject } from '@bpinternal/schemer/types'
import { Zap, Database, Activity } from 'lucide-react'
import { keys } from 'lodash'
import { ComponentProps, useState } from 'react'
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@bpinternal/ui-kit-next'
import { splitCamelCase } from '../../../utils'
import { cn } from '~/utils'
import { CardGrid } from './CardGrid'
import { CardGridItem } from './CardGridItem'
import { DetailSection } from './DetailSection'

type IntegrationDetailsProps = {
  integration: Integration
} & ComponentProps<'div'>

type Item<T> = { name: string; type: T }

export function IntegrationDetails({ integration, ...props }: IntegrationDetailsProps): JSX.Element {
  const states = keys(integration.states).map((key) => ({
    name: key,
    ...integration.states?.[key],
  }))

  const userItems: Item<'state' | 'tag'>[] = [
    ...states.filter((state) => state.type === 'user').map((state) => ({ name: state.name, type: 'state' }) as const),
    ...keys(integration.user.tags).map((tag) => ({ name: tag, type: 'tag' }) as const),
  ]
  const [selectedSchema, setSelectedSchema] = useState<any>(undefined)

  const actionItems = Object.entries(integration.actions).map(([key, value]) => ({
    name: key,
    action: value,
    type: 'action',
  }))
  const [selectedAction, setSelectedAction] = useState<(typeof actionItems)[number]['action'] | undefined>(undefined)

  const eventItems = Object.entries(integration.events).map(([key, value]) => ({
    name: key,
    type: 'event',
    schema: value.schema,
  }))

  const globalStateItems = states
    .filter((state) => state.type !== 'user' && state.type !== 'conversation')
    .map((state) => ({ name: state.name, type: 'state', schema: state.schema }) as const)

  const conversationStates = states
    .filter((state) => state.type === 'conversation')
    .map((state) => ({ name: state.name, type: 'state' }) as const)

  function onActionItemClick(action: (typeof actionItems)[number]['action']): void {
    setSelectedAction(action)
  }

  function onSchemaItemClick(item: any): void {
    setSelectedSchema({ title: item.name, schema: item.schema })
  }

  function cardGridItemWithSchemaPopover(item: (typeof globalStateItems)[number] | (typeof eventItems)[number]) {
    return (
      <Popover key={item.name}>
        <PopoverTrigger>
          <CardGridItem onClick={() => onSchemaItemClick(item)} title={item.name} item={item} isClickable />
        </PopoverTrigger>
        <PopoverContent className="w-[300px]">
          {selectedSchema && <SchemerTree title={selectedSchema.title} schemaObject={selectedSchema?.schema} />}
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <div {...props} className={cn('mx-auto flex w-full flex-col gap-8 pb-8 pt-2', props.className)}>
      {keys(integration.channels).length > 0 && (
        <Accordion type="multiple" defaultValue={[keys(integration.channels)?.[0] ?? '']}>
          {keys(integration.channels).map((name) => {
            const channelItems = keys(integration.channels[name]?.messages).map(
              (message) =>
                ({
                  name: message,
                  type: message,
                }) as const
            )

            const messageItems = keys(integration.channels[name]?.message.tags).map(
              (tag) => ({ name: tag, type: 'tag' }) as const
            )

            const conversationItems = [
              ...keys(integration.channels[name]?.conversation.tags).map(
                (tag) => ({ name: tag, type: 'tag' }) as const
              ),
              ...conversationStates,
            ]

            return (
              <AccordionItem className="last:border-b" key={name} value={name}>
                <AccordionTrigger className={'rounded-none capitalize'}>{splitCamelCase(name)}</AccordionTrigger>
                <AccordionContent>
                  <div className="relative flex flex-col gap-4 rounded-lg p-2">
                    {channelItems.length > 0 && (
                      <DetailSection title="Message types" description="This integration supports these message types">
                        <CardGrid items={channelItems}>
                          {(item) => <CardGridItem key={item.name} title={item.name} item={item} />}
                        </CardGrid>
                      </DetailSection>
                    )}
                    {messageItems.length > 0 && (
                      <DetailSection
                        title="Message settings"
                        description="These tags are provided with messages from the integration"
                      >
                        <CardGrid items={messageItems}>
                          {(item) => <CardGridItem key={item.name} title={item.name} item={item} />}
                        </CardGrid>
                      </DetailSection>
                    )}
                    {conversationItems.length > 0 && (
                      <DetailSection
                        title="Conversation settings"
                        description={
                          integration.user.creation.enabled
                            ? 'This integration creates new conversations'
                            : 'This integration does not create new conversations'
                        }
                      >
                        {conversationItems.length > 0 && (
                          <CardGrid items={conversationItems}>
                            {(item) => <CardGridItem key={item.name} title={item.name} item={item} />}
                          </CardGrid>
                        )}
                      </DetailSection>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )
          })}
        </Accordion>
      )}

      {actionItems.length > 0 && (
        <DetailSection
          title="Actions"
          icon={<Zap className="mr-1 h-4 w-4 text-primary-800" />}
          description="List of actions defined by the integration"
        >
          <CardGrid items={actionItems}>
            {(item) => (
              <Popover key={item.name}>
                <PopoverTrigger>
                  <CardGridItem
                    isClickable
                    onClick={() => onActionItemClick(item.action)}
                    title={item.name}
                    item={item}
                  />
                </PopoverTrigger>
                <PopoverContent className="w-[300px] space-y-2">
                  {selectedAction && (
                    <>
                      <SchemerTree title="Input" schemaObject={selectedAction.input.schema as OpenApiSchemaObject} />
                      <SchemerTree title="Output" schemaObject={selectedAction.output.schema as OpenApiSchemaObject} />
                    </>
                  )}
                </PopoverContent>
              </Popover>
            )}
          </CardGrid>
        </DetailSection>
      )}

      {eventItems.length > 0 && (
        <DetailSection
          title="Events"
          icon={<Activity className="mr-1 h-4 w-4 text-green-600" />}
          description="List of events defined by the integration"
        >
          <CardGrid items={eventItems}>{cardGridItemWithSchemaPopover}</CardGrid>
        </DetailSection>
      )}

      {globalStateItems.length > 0 && (
        <DetailSection
          title="Global states"
          icon={<Database className="mr-1 h-4 w-4 text-red-600" />}
          description="States globally available in your integration"
        >
          <CardGrid items={globalStateItems}>{cardGridItemWithSchemaPopover}</CardGrid>
        </DetailSection>
      )}

      <DetailSection
        title="User settings"
        description={
          integration.user.creation.enabled
            ? 'This integration creates new users'
            : 'This integration does not create new users'
        }
      >
        <CardGrid items={userItems}>
          {(item) => <CardGridItem key={item.name} title={item.name} item={item} />}
        </CardGrid>
      </DetailSection>
    </div>
  )
}
